import { useState, useCallback, useMemo } from "react";
import {
  partyLinks,
  partyReports,
  partiesSummary,
} from "@/mocks/partes";
import type { Party } from "@/mocks/partes";
import { usePartiesList } from "@/hooks";
import PartiesKpiCard from "./components/PartiesKpiCard";
import PartiesFilters from "./components/PartiesFilters";
import PartiesTable from "./components/PartiesTable";
import PartyCard from "./components/PartyCard";
import PartyDetailDrawer from "./components/PartyDetailDrawer";
import NewPartyModal from "./components/NewPartyModal";
import NewPartyLinkModal from "./components/NewPartyLinkModal";
import PartyRolesTable from "./components/PartyRolesTable";
import TechnicalResponsiblePanel from "./components/TechnicalResponsiblePanel";
import InstitutionsTable from "./components/InstitutionsTable";
import CompaniesTable from "./components/CompaniesTable";
import ProjectPartiesPanel from "./components/ProjectPartiesPanel";
import EnvironmentalUnitPartiesTable from "./components/EnvironmentalUnitPartiesTable";
import PartyReportsGrid from "./components/PartyReportsGrid";
import PartyQualityPanel from "./components/PartyQualityPanel";

const tabs = [
  { id: "cadastro", label: "Cadastro de Partes" },
  { id: "papeis", label: "Papéis e Vínculos" },
  { id: "tecnicos", label: "Responsáveis Técnicos" },
  { id: "orgaos", label: "Órgãos e Instituições" },
  { id: "empresas", label: "Empresas e Contratantes" },
  { id: "projetos", label: "Vínculos por Projeto" },
  { id: "ambientais", label: "Vínculos por Unidade Ambiental" },
  { id: "relatorios", label: "Relatórios" },
];

const initialFilters = {
  search: "",
  type: "",
  role: "",
  status: "",
  municipality: "",
  hasDocs: "",
  hasLink: "",
  isTech: "",
};

export default function Partes() {
  const [activeTab, setActiveTab] = useState("cadastro");
  const [selectedParty, setSelectedParty] = useState<Party | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkPreselected, setLinkPreselected] = useState<string | undefined>(undefined);
  const [showCards, setShowCards] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const { items: parties } = usePartiesList();

  const handleFiltersChange = useCallback(
    (next: Record<string, string>) => setFilters((prev) => ({ ...prev, ...next })),
    []
  );

  const handleView = useCallback((party: Party) => setSelectedParty(party), []);
  const handleCloseDrawer = useCallback(() => setSelectedParty(null), []);

  const handleNewParty = useCallback(() => setShowNewModal(true), []);
  const handleCloseNew = useCallback(() => setShowNewModal(false), []);
  const handleSubmitNew = useCallback((data: Record<string, string>) => {
    console.log("Nova parte:", data);
  }, []);

  const handleNewLink = useCallback((party?: Party) => {
    setLinkPreselected(party?.name);
    setShowLinkModal(true);
  }, []);
  const handleCloseLink = useCallback(() => {
    setShowLinkModal(false);
    setLinkPreselected(undefined);
  }, []);
  const handleSubmitLink = useCallback((data: Record<string, string>) => {
    console.log("Novo vínculo:", data);
  }, []);

  const filteredParties = useMemo(() => {
    return parties.filter((p) => {
      if (filters.search) {
        const s = filters.search.toLowerCase();
        if (!p.name.toLowerCase().includes(s) && !p.documentId.includes(s) && !p.email.toLowerCase().includes(s) && !p.phone.includes(s)) return false;
      }
      if (filters.type && p.type !== filters.type) return false;
      if (filters.role && p.primaryRole !== filters.role) return false;
      if (filters.status && p.status !== filters.status) return false;
      if (filters.municipality && !p.municipality.toLowerCase().includes(filters.municipality.toLowerCase())) return false;
      if (filters.hasDocs === "yes" && !p.hasDocuments) return false;
      if (filters.hasLink === "yes" && p.activeLinks === 0) return false;
      if (filters.isTech === "yes" && p.primaryRole !== "Responsável técnico") return false;
      return true;
    });
  }, [filters, parties]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="text-xs text-foreground-500 mb-1">Dashboard &gt; Partes Envolvidas</div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
            Partes Envolvidas
          </h1>
          <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
            Gerencie pessoas, órgãos, empresas e responsáveis vinculados ao CTA, projetos ambientais, documentos, autorizações e obrigações.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleNewParty}
            className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Nova parte
          </button>
          <button
            onClick={() => handleNewLink()}
            className="px-4 py-2 text-sm font-semibold text-accent-700 bg-accent-50 rounded-lg hover:bg-accent-100 transition-colors whitespace-nowrap"
          >
            <i className="ri-link-m w-4 h-4 inline-flex items-center justify-center mr-1" />
            Novo vínculo
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-secondary-700 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors whitespace-nowrap">
            <i className="ri-upload-cloud-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Importar contatos
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
            <i className="ri-download-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Exportar lista
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
            <i className="ri-file-chart-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Gerar relatório
          </button>
        </div>
      </div>

      {/* Conceptual notice */}
      <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-secondary-600 w-full h-full flex items-center justify-center text-lg" />
          </div>
          <p className="text-xs text-secondary-700 leading-relaxed">
            As partes envolvidas representam <strong>pessoas, instituições ou empresas</strong> que possuem responsabilidade, vínculo técnico, obrigação, autorização ou relação documental com unidades ambientais do FloraIQ — conforme modelo <strong>LADM-FloraIQ</strong>: Party → Role → Source → Spatial Unit → RRR.
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <PartiesKpiCard label="Partes cadastradas" value="428" icon="ri-team-line" color="primary" subtitle="Total no sistema" />
        <PartiesKpiCard label="Responsáveis técnicos" value={String(partiesSummary.technicalResponsibles)} icon="ri-medal-line" color="primary" subtitle="Com registro profissional" />
        <PartiesKpiCard label="Órgãos públicos" value={String(partiesSummary.publicOrgs)} icon="ri-government-line" color="accent" subtitle="Prefeituras, secretarias, etc." />
        <PartiesKpiCard label="Empresas executoras" value="42" icon="ri-building-line" color="accent" subtitle="Consultorias e prestadoras" />
        <PartiesKpiCard label="Partes com vínculo ativo" value={String(partiesSummary.withActiveLinks)} icon="ri-link-m" color="secondary" subtitle="Vínculos ativos no sistema" />
        <PartiesKpiCard label="Vínculos pendentes" value={String(partiesSummary.pendingLinks)} icon="ri-time-line" color="warning" subtitle="Aguardando definição" />
        <PartiesKpiCard label="Partes sem documento" value={String(partiesSummary.withoutDocs)} icon="ri-file-warning-line" color="warning" subtitle="Precisam de documentação" />
        <PartiesKpiCard label="Obrigações atribuídas" value={String(partiesSummary.totalObligations)} icon="ri-survey-line" color="secondary" subtitle="Responsabilidades ativas" />
      </div>

      {/* Tabs */}
      <div className="border-b border-background-200">
        <div className="flex gap-1 overflow-x-auto pb-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors border-b-2 ${
                activeTab === tab.id
                  ? "text-primary-700 border-primary-500 bg-primary-50/50"
                  : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {/* Cadastro de Partes */}
        {activeTab === "cadastro" && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground-900">Cadastro de partes</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCards(false)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!showCards ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                >
                  <i className="ri-table-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Tabela
                </button>
                <button
                  onClick={() => setShowCards(true)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${showCards ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                >
                  <i className="ri-grid-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Cards
                </button>
              </div>
            </div>

            <PartiesFilters filters={filters} onChange={handleFiltersChange} />

            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-filter-line w-4 h-4 flex items-center justify-center" />
              <span>{filteredParties.length} de {parties.length} partes encontradas</span>
            </div>

            {showCards ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredParties.map((party) => (
                  <PartyCard key={party.id} party={party} onView={handleView} />
                ))}
              </div>
            ) : (
              <PartiesTable
                parties={filteredParties}
                onView={handleView}
                onEdit={handleView}
                onLink={(p) => handleNewLink(p)}
              />
            )}

            {filteredParties.length === 0 && (
              <div className="text-center py-12 text-foreground-400">
                <i className="ri-user-search-line text-4xl mb-3 block" />
                <p className="text-sm">Nenhuma parte encontrada com os filtros atuais.</p>
              </div>
            )}
          </div>
        )}

        {/* Papéis e Vínculos */}
        {activeTab === "papeis" && <PartyRolesTable />}

        {/* Responsáveis Técnicos */}
        {activeTab === "tecnicos" && <TechnicalResponsiblePanel />}

        {/* Órgãos e Instituições */}
        {activeTab === "orgaos" && <InstitutionsTable />}

        {/* Empresas e Contratantes */}
        {activeTab === "empresas" && <CompaniesTable />}

        {/* Vínculos por Projeto */}
        {activeTab === "projetos" && <ProjectPartiesPanel />}

        {/* Vínculos por Unidade Ambiental */}
        {activeTab === "ambientais" && <EnvironmentalUnitPartiesTable />}

        {/* Relatórios */}
        {activeTab === "relatorios" && (
          <div className="space-y-5">
            <PartyReportsGrid reports={partyReports} />
            <PartyQualityPanel />
          </div>
        )}
      </div>

      {/* Drawer */}
      {selectedParty && (
        <PartyDetailDrawer party={selectedParty} onClose={handleCloseDrawer} />
      )}

      {/* New Party Modal */}
      {showNewModal && (
        <NewPartyModal onClose={handleCloseNew} onSubmit={handleSubmitNew} />
      )}

      {/* New Link Modal */}
      {showLinkModal && (
        <NewPartyLinkModal
          onClose={handleCloseLink}
          onSubmit={handleSubmitLink}
          preselectedParty={linkPreselected}
        />
      )}
    </div>
  );
}